import React, { useEffect, useState, useCallback } from "react";
import { Form, Button, Space, Card, Divider } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "Store/hooks";
import { useNavigate, useParams } from "react-router-dom";
import {
  selectCurrentTenantId,
  selectPackageNames,
  selectTierOptions,
  selectPackageInfos,
  selectLicenseSettings,
  selectSupportedFileFormats,
} from "../redux/selectors";
import { setCurrentTenantId } from "../redux/slice";
import {
  createTenantLicense,
  getLicenseTiers,
  getPackageNames,
  getLicenseSettings,
  getSupportedFileFormats,
} from "../redux/thunks";
import {
  getPackageNameFromIndex,
  mapLicenseSettings,
  extractFeatureGroupNames,
  parseExpiryDate,
} from "../utils/licenseUtils";
import { setErrorState } from "Components/Basic/ErrorBoundary/redux/slice";
import { setLoadingState } from "Components/Basic/full_page_loader/redux/slice";
import {
  ICreateTenantLicenseRequest,
  ILicenseRestrictions,
  ILicenseSettings,
} from "../redux/types";
import Stepper from "Components/Custom/Stepper";
import FeatureSelectionModal from "Features/SubscriptionConfiguration/FeatureGroups/FeatureSelectionModal/FeatureSelectionModal.index";
import { getAllFeatureGroups } from "Features/SubscriptionConfiguration/FeatureGroups/redux/thunks";
import { selectFeatureGroupList } from "Features/SubscriptionConfiguration/FeatureGroups/redux/selectors";
import { getAllPaddleProducts } from "Features/SubscriptionConfiguration/PaddleProducts/redux/thunks";
import { selectPaddleProductList } from "Features/SubscriptionConfiguration/PaddleProducts/redux/selectors";
import { SubscriptionProductEdition } from "Features/SubscriptionConfiguration/SubscriptionTiers/redux/types";
import { getAllTenants } from "../../redux/thunks";
import { ITenantFilter, ITenant } from "../../redux/types";
import { selectTenants } from "../../redux/selectors";
import dayjs from "dayjs";
import BasicConfigurationStep from "./BasicConfigurationStep";
import LicenseSettingsStep from "./LicenseSettingsStep";
import FeatureGroupsStep from "./FeatureGroupsStep";
import "./index.css";

export type LicenseSourceType = "custom" | "subscription";

const AddEditLicensePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const currentTenantId = useAppSelector(selectCurrentTenantId);
  const packageNames = useAppSelector(selectPackageNames);
  const packageInfos = useAppSelector(selectPackageInfos);
  const licenseSettings = useAppSelector(selectLicenseSettings);
  const tierOptions = useAppSelector(selectTierOptions);
  const supportedFileFormats = useAppSelector(selectSupportedFileFormats);
  const featureGroups = useAppSelector(selectFeatureGroupList);
  const tenantList = useAppSelector(selectTenants);
  const paddleProducts = useAppSelector(selectPaddleProductList);

  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFeatureGroups, setSelectedFeatureGroups] = useState<string[]>(
    []
  );
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>(
    []
  );
  const [licenseSource, setLicenseSource] = useState<LicenseSourceType>("custom");

  const steps = ["Basic Configuration", "Settings", "Feature Groups"];

  const { subscriptionPrices, productName: paddleProductName } = React.useMemo(() => {
    const docspireProduct = paddleProducts.find(
      (p) => p.productEdition === SubscriptionProductEdition.Docspire
    );
    if (!docspireProduct) return { subscriptionPrices: [], productName: "" };
    return {
      subscriptionPrices: docspireProduct.prices.filter(
        (price) =>
          price.isActive &&
          (price.priceType === "subscription" ||
            price.customData?.price_type === "subscription")
      ),
      productName: docspireProduct.name,
    };
  }, [paddleProducts]);

  const handleLicenseSourceChange = useCallback(
    (source: LicenseSourceType) => {
      setLicenseSource(source);
      if (source === "custom") {
        form.setFieldsValue({
          subscriptionPrice: undefined,
        });
      }
    },
    [form]
  );

  const handleSubscriptionPriceSelect = useCallback(
    (priceId: string) => {
      const selectedPrice = subscriptionPrices.find(
        (p) => p.priceId === priceId
      );
      if (!selectedPrice) return;

      const customData = selectedPrice.customData || {};
      const priceName = customData.price_name || "";

      const matchedTier = tierOptions.find(
        (t) => t.name.toLowerCase() === priceName.toLowerCase()
      );
      if (matchedTier) {
        form.setFieldsValue({ tierSelection: matchedTier.name });
      }

      const interval = selectedPrice.billingInterval?.toLowerCase() || "";
      let expiryDate;
      if (interval.includes("annual") || interval.includes("year")) {
        expiryDate = dayjs().add(1, "year");
      } else if (interval.includes("month")) {
        expiryDate = dayjs().add(1, "month");
      }
      if (expiryDate) {
        form.setFieldsValue({ expiryDate });
      }

      const limits = customData.usage_limits;
      if (limits && typeof limits === "object") {
        const restrictionKeys: string[] = [];
        const restrictionValues: Record<string, number> = {};

        const limitMappings: Array<{
          snakeKey: string;
          camelKey: string;
          formKey: string;
        }> = [
          { snakeKey: "max_users", camelKey: "maxUsers", formKey: "maxUsers" },
          { snakeKey: "max_ai_tokens", camelKey: "maxAiTokens", formKey: "maxAiTokens" },
          { snakeKey: "max_scheduled_jobs", camelKey: "maxScheduledJobs", formKey: "maxScheduledJobs" },
          { snakeKey: "max_rows_allowed", camelKey: "maxRowsAllowed", formKey: "maxRowsAllowed" },
          { snakeKey: "max_pages_allowed", camelKey: "maxPagesAllowed", formKey: "maxPagesAllowed" },
          { snakeKey: "max_validation_rules_allowed", camelKey: "maxValidationRulesAllowed", formKey: "maxValidationRulesAllowed" },
          { snakeKey: "max_api_connectors_allowed", camelKey: "maxApiConnectorsAllowed", formKey: "maxApiConnectorsAllowed" },
          { snakeKey: "max_automation_workflows_allowed", camelKey: "maxAutomationWorkflowsAllowed", formKey: "maxAutomationWorkflowsAllowed" },
          { snakeKey: "max_email_integrations_allowed", camelKey: "maxEmailIntegrationsAllowed", formKey: "maxEmailIntegrationsAllowed" },
          { snakeKey: "max_notification_recipients_allowed", camelKey: "maxNotificationRecipientsAllowed", formKey: "maxNotificationRecipientsAllowed" },
        ];

        limitMappings.forEach(({ snakeKey, camelKey, formKey }) => {
          const val = limits[snakeKey] ?? limits[camelKey];
          if (val != null && val > -1) {
            restrictionKeys.push(formKey);
            restrictionValues[formKey] = val;
          }
        });

        const formatValues: Record<string, string[]> = {};
        const inputFmts = limits["supported_input_formats"] ?? limits["supportedInputFormats"];
        if (Array.isArray(inputFmts) && inputFmts.length > 0) {
          restrictionKeys.push("supportedInputFormats");
          formatValues.supportedInputFormats = inputFmts;
        }
        const outputFmts = limits["supported_output_formats"] ?? limits["supportedOutputFormats"];
        if (Array.isArray(outputFmts) && outputFmts.length > 0) {
          restrictionKeys.push("supportedOutputFormats");
          formatValues.supportedOutputFormats = outputFmts;
        }

        setSelectedRestrictions(restrictionKeys);
        form.setFieldsValue({ restrictions: restrictionKeys });

        setTimeout(() => {
          form.setFieldsValue({ ...restrictionValues, ...formatValues });
        }, 0);
      } else {
        setSelectedRestrictions([]);
        form.setFieldsValue({ restrictions: [] });
      }

      let featureGroupNames: string[] = [];
      if (customData.feature_groups) {
        if (Array.isArray(customData.feature_groups)) {
          featureGroupNames = customData.feature_groups;
        } else if (typeof customData.feature_groups === "string") {
          try {
            const parsed = JSON.parse(customData.feature_groups);
            featureGroupNames = Array.isArray(parsed) ? parsed : [];
          } catch {
            featureGroupNames = [customData.feature_groups];
          }
        }
      }

      if (featureGroupNames.length > 0 && featureGroups) {
        const featureGroupIds = featureGroupNames
          .map((name) => {
            const fg = featureGroups.find(
              (g) => g.name === name || g.displayName === name
            );
            return fg?.id;
          })
          .filter((id): id is string => id !== undefined);
        setSelectedFeatureGroups(featureGroupIds);
      } else {
        setSelectedFeatureGroups([]);
      }
    },
    [subscriptionPrices, tierOptions, featureGroups, form]
  );

  useEffect(() => {
    const tenantIdNum = tenantId ? parseInt(tenantId, 10) : null;
    if (tenantIdNum) {
      dispatch(setCurrentTenantId(tenantIdNum));
    }
    dispatch(getAllFeatureGroups());
    dispatch(getLicenseTiers());
    dispatch(getPackageNames());
    dispatch(getLicenseSettings());
    dispatch(getSupportedFileFormats());
    dispatch(getAllPaddleProducts());

    if (tenantList.length === 0) {
      const tenantFilter: ITenantFilter = { name: null };
      dispatch(getAllTenants(tenantFilter));
    }
  }, [tenantId, dispatch, tenantList.length]);

  useEffect(() => {
    const tenantIdNum = tenantId ? parseInt(tenantId, 10) : null;
    
    if (
      tenantIdNum &&
      tenantList.length > 0 &&
      tierOptions.length > 0 &&
      packageInfos.length > 0 &&
      licenseSettings.length > 0 &&
      featureGroups
    ) {
      const tenant = tenantList.find((t: ITenant) => t.id === tenantIdNum);
      if (tenant?.license) {
        try {
          const packageName = getPackageNameFromIndex(
            tenant.license.packageIndex,
            packageInfos
          );

          const tierName = tierOptions.find(
            (t) => t.value === tenant.license.tier
          )?.name;

          const expiryDate = parseExpiryDate(tenant.license.expirationDate);

          const settings = mapLicenseSettings(
            tenant.license.settings,
            licenseSettings
          );

          const featureGroupNames = extractFeatureGroupNames(
            tenant.license.features
          );

          const featureGroupIds = featureGroupNames
            .map((name: string) => {
              const group = featureGroups.find(
                (fg) => fg.name === name || fg.displayName === name
              );
              return group?.id;
            })
            .filter((id): id is string => id !== undefined);
          
          setSelectedFeatureGroups(featureGroupIds);

          const usageMetrics = tenant.license.usageMetrics;
          const restrictionKeys: string[] = [];
          const restrictionValues: any = {};
          
          if (usageMetrics) {
            if (usageMetrics.maxUsers && usageMetrics.maxUsers > 0) {
              restrictionKeys.push("maxUsers");
              restrictionValues.maxUsers = usageMetrics.maxUsers;
            }
            if (usageMetrics.maxAiTokens && usageMetrics.maxAiTokens > 0) {
              restrictionKeys.push("maxAiTokens");
              restrictionValues.maxAiTokens = usageMetrics.maxAiTokens;
            }
            if (usageMetrics.maxScheduledJobs && usageMetrics.maxScheduledJobs > 0) {
              restrictionKeys.push("maxScheduledJobs");
              restrictionValues.maxScheduledJobs = usageMetrics.maxScheduledJobs;
            }
            if (usageMetrics.maxRowsAllowed && usageMetrics.maxRowsAllowed > 0) {
              restrictionKeys.push("maxRowsAllowed");
              restrictionValues.maxRowsAllowed = usageMetrics.maxRowsAllowed;
            }
            if (usageMetrics.maxPagesAllowed && usageMetrics.maxPagesAllowed > 0) {
              restrictionKeys.push("maxPagesAllowed");
              restrictionValues.maxPagesAllowed = usageMetrics.maxPagesAllowed;
            }
            if (usageMetrics.maxValidationRulesAllowed && usageMetrics.maxValidationRulesAllowed > 0) {
              restrictionKeys.push("maxValidationRulesAllowed");
              restrictionValues.maxValidationRulesAllowed = usageMetrics.maxValidationRulesAllowed;
            }
            if (usageMetrics.maxApiConnectorsAllowed && usageMetrics.maxApiConnectorsAllowed > 0) {
              restrictionKeys.push("maxApiConnectorsAllowed");
              restrictionValues.maxApiConnectorsAllowed = usageMetrics.maxApiConnectorsAllowed;
            }
            if (usageMetrics.maxAutomationWorkflowsAllowed && usageMetrics.maxAutomationWorkflowsAllowed > 0) {
              restrictionKeys.push("maxAutomationWorkflowsAllowed");
              restrictionValues.maxAutomationWorkflowsAllowed = usageMetrics.maxAutomationWorkflowsAllowed;
            }
            if (usageMetrics.maxEmailIntegrationsAllowed && usageMetrics.maxEmailIntegrationsAllowed > 0) {
              restrictionKeys.push("maxEmailIntegrationsAllowed");
              restrictionValues.maxEmailIntegrationsAllowed = usageMetrics.maxEmailIntegrationsAllowed;
            }
            if (usageMetrics.maxNotificationRecipientsAllowed && usageMetrics.maxNotificationRecipientsAllowed > 0) {
              restrictionKeys.push("maxNotificationRecipientsAllowed");
              restrictionValues.maxNotificationRecipientsAllowed = usageMetrics.maxNotificationRecipientsAllowed;
            }
            const inputFmts = usageMetrics.supportedInputFormats ?? (usageMetrics as any).supported_input_formats;
            if (Array.isArray(inputFmts) && inputFmts.length > 0) {
              restrictionKeys.push("supportedInputFormats");
              restrictionValues.supportedInputFormats = inputFmts;
            }
            const outputFmts = usageMetrics.supportedOutputFormats ?? (usageMetrics as any).supported_output_formats;
            if (Array.isArray(outputFmts) && outputFmts.length > 0) {
              restrictionKeys.push("supportedOutputFormats");
              restrictionValues.supportedOutputFormats = outputFmts;
            }
          }
          
          setSelectedRestrictions(restrictionKeys);

          const existingSourcePriceId = tenant.license.sourcePriceId;
          const hasMatchingPrice =
            existingSourcePriceId &&
            subscriptionPrices.length > 0 &&
            subscriptionPrices.some((p) => p.priceId === existingSourcePriceId);

          if (hasMatchingPrice) {
            setLicenseSource("subscription");
          }

          form.setFieldsValue({
            packageName: packageName || undefined,
            tierSelection: tierName || undefined,
            expiryDate: expiryDate,
            restrictions: restrictionKeys,
            allowBetaFeatures: settings.allowBetaFeatures,
            allowEmbeddedServer: settings.allowEmbeddedServer,
            allowLicensePicker: settings.allowLicensePicker,
            isDevModeEnabled: settings.isDevModeEnabled,
            isNonProduction: settings.isNonProduction,
            isOem: settings.isOem,
            isTrial: settings.isTrial,
            isTimeLimit: settings.isTimeLimit,
            isOfflineKey: settings.isOfflineKey,
            isRestrictedTrial: settings.isRestrictedTrial,
            ...restrictionValues,
          });

          if (hasMatchingPrice) {
            setTimeout(() => {
              form.setFieldsValue({ subscriptionPrice: existingSourcePriceId });
            }, 0);
          }
        } catch (error) {
          console.error("Error loading license data:", error);
          form.resetFields();
          setCurrentStep(0);
          setSelectedFeatureGroups([]);
          setSelectedRestrictions([]);
        }
      } else {
        form.resetFields();
        setCurrentStep(0);
        setSelectedFeatureGroups([]);
        setSelectedRestrictions([]);
      }
    } else if (!tenantIdNum) {
      form.resetFields();
      setCurrentStep(0);
      setSelectedFeatureGroups([]);
      setSelectedRestrictions([]);
    }
  }, [
    tenantId,
    tenantList,
    tierOptions,
    packageInfos,
    licenseSettings,
    featureGroups,
    subscriptionPrices,
    supportedFileFormats,
    form,
  ]);

  const handleCancel = () => {
    navigate("/tenants");
    dispatch(setCurrentTenantId(null));
    form.resetFields();
    setCurrentStep(0);
    setSelectedFeatureGroups([]);
    setSelectedRestrictions([]);
  };

  const handleNext = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      const fieldNames =
        currentStep === 0
          ? [
              "packageName",
              "tierSelection",
              "restrictions",
              "maxUsers",
              "maxAiTokens",
              "maxScheduledJobs",
              "maxRowsAllowed",
              "maxPagesAllowed",
              "maxValidationRulesAllowed",
              "maxApiConnectorsAllowed",
              "maxAutomationWorkflowsAllowed",
              "maxEmailIntegrationsAllowed",
              "maxNotificationRecipientsAllowed",
              ...(licenseSource === "subscription" ? ["subscriptionPrice"] : []),
            ]
          : currentStep === 1
          ? [
              "expiryDate",
              "allowBetaFeatures",
              "allowEmbeddedServer",
              "allowLicensePicker",
              "isDevModeEnabled",
              "isNonProduction",
              "isOem",
              "isTrial",
              "isTimeLimit",
              "isOfflineKey",
              "isRestrictedTrial",
            ]
          : [];

      await form.validateFields(fieldNames);

      if (currentStep === 0) {
        setCurrentStep(1);
      } else if (currentStep === 1) {
        setCurrentStep(2);
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCurrentStep(currentStep - 1);
  };

  const handleFeatureConfirm = (selectedIds: string[]) => {
    setSelectedFeatureGroups(selectedIds);
    setIsFeatureModalOpen(false);
  };

  const handleRestrictionChange = (checkedValues: string[]) => {
    setSelectedRestrictions(checkedValues);

    const restrictionFields = [
      "maxUsers",
      "maxAiTokens",
      "maxScheduledJobs",
      "maxRowsAllowed",
      "maxPagesAllowed",
      "maxValidationRulesAllowed",
      "maxApiConnectorsAllowed",
      "maxAutomationWorkflowsAllowed",
      "maxEmailIntegrationsAllowed",
      "maxNotificationRecipientsAllowed",
      "supportedInputFormats",
      "supportedOutputFormats",
    ];

    restrictionFields.forEach((field) => {
      if (!checkedValues.includes(field)) {
        form.setFieldValue(field, undefined);
      }
    });
  };

  const onFinish = async () => {
    try {
      if (currentStep !== 2) {
        return;
      }

      const values = await form.validateFields();

      const effectiveTenantId = tenantId
        ? parseInt(tenantId, 10)
        : currentTenantId;

      if (!effectiveTenantId) {
        dispatch(
          setErrorState({
            message: "Error",
            description: "Tenant ID is required",
            placement: "topRight",
            type: "error",
          })
        );
        return;
      }

      const restrictions: ILicenseRestrictions = {};
      if (
        selectedRestrictions.includes("maxUsers") &&
        values.maxUsers != null
      ) {
        restrictions.maxUsers = Number(values.maxUsers);
      }
      if (
        selectedRestrictions.includes("maxAiTokens") &&
        values.maxAiTokens != null
      ) {
        restrictions.maxAiTokens = Number(values.maxAiTokens);
      }
      if (
        selectedRestrictions.includes("maxScheduledJobs") &&
        values.maxScheduledJobs != null
      ) {
        restrictions.maxScheduledJobs = Number(values.maxScheduledJobs);
      }
      if (
        selectedRestrictions.includes("maxRowsAllowed") &&
        values.maxRowsAllowed != null
      ) {
        restrictions.maxRowsAllowed = Number(values.maxRowsAllowed);
      }
      if (
        selectedRestrictions.includes("maxPagesAllowed") &&
        values.maxPagesAllowed != null
      ) {
        restrictions.maxPagesAllowed = Number(values.maxPagesAllowed);
      }
      if (
        selectedRestrictions.includes("maxValidationRulesAllowed") &&
        values.maxValidationRulesAllowed != null
      ) {
        restrictions.maxValidationRulesAllowed = Number(values.maxValidationRulesAllowed);
      }
      if (
        selectedRestrictions.includes("maxApiConnectorsAllowed") &&
        values.maxApiConnectorsAllowed != null
      ) {
        restrictions.maxApiConnectorsAllowed = Number(values.maxApiConnectorsAllowed);
      }
      if (
        selectedRestrictions.includes("maxAutomationWorkflowsAllowed") &&
        values.maxAutomationWorkflowsAllowed != null
      ) {
        restrictions.maxAutomationWorkflowsAllowed = Number(values.maxAutomationWorkflowsAllowed);
      }
      if (
        selectedRestrictions.includes("maxEmailIntegrationsAllowed") &&
        values.maxEmailIntegrationsAllowed != null
      ) {
        restrictions.maxEmailIntegrationsAllowed = Number(values.maxEmailIntegrationsAllowed);
      }
      if (
        selectedRestrictions.includes("maxNotificationRecipientsAllowed") &&
        values.maxNotificationRecipientsAllowed != null
      ) {
        restrictions.maxNotificationRecipientsAllowed = Number(values.maxNotificationRecipientsAllowed);
      }
      if (
        selectedRestrictions.includes("supportedInputFormats") &&
        values.supportedInputFormats?.length
      ) {
        restrictions.supportedInputFormats = values.supportedInputFormats;
      }
      if (
        selectedRestrictions.includes("supportedOutputFormats") &&
        values.supportedOutputFormats?.length
      ) {
        restrictions.supportedOutputFormats = values.supportedOutputFormats;
      }

      const settings: ILicenseSettings = {
        allowBetaFeatures: values.allowBetaFeatures ?? false,
        allowEmbeddedServer: values.allowEmbeddedServer ?? false,
        allowLicensePicker: values.allowLicensePicker ?? false,
        isDevModeEnabled: values.isDevModeEnabled ?? false,
        isNonProduction: values.isNonProduction ?? false,
        isOem: values.isOem ?? false,
        isTrial: values.isTrial ?? false,
        isTimeLimit: values.isTimeLimit ?? false,
        isOfflineKey: values.isOfflineKey ?? false,
        isRestrictedTrial: values.isRestrictedTrial ?? false,
      };

      let tierSelectionValue: string | undefined = undefined;
      if (values.tierSelection != null) {
        tierSelectionValue =
          typeof values.tierSelection === "number"
            ? String(values.tierSelection)
            : values.tierSelection;
      }

      let expiryDateValue: string | undefined = undefined;
      if (values.expiryDate) {
        if (dayjs.isDayjs(values.expiryDate)) {
          expiryDateValue = values.expiryDate.toISOString();
        } else if (typeof values.expiryDate === "string") {
          expiryDateValue = dayjs(values.expiryDate).toISOString();
        }
      }

      const featureGroupNames = selectedFeatureGroups
        .map((id: string) => {
          const feature = featureGroups?.find((fg) => fg.id === id);
          return feature?.name;
        })
        .filter(
          (name: string | undefined): name is string => name !== undefined
        );

      const request: ICreateTenantLicenseRequest = {
        tenantId: effectiveTenantId,
        packageName: values.packageName,
        tierSelection: tierSelectionValue,
        restrictions:
          Object.keys(restrictions).length > 0 ? restrictions : undefined,
        expiryDate: expiryDateValue,
        settings: settings,
        featureGroups: featureGroupNames || [],
        sourcePriceId:
          licenseSource === "subscription" ? values.subscriptionPrice : undefined,
      };

      dispatch(setLoadingState(true));
      await dispatch(createTenantLicense(request)).unwrap();

      dispatch(
        setErrorState({
          message: "Success",
          description: "License created/updated successfully",
          placement: "topRight",
          type: "success",
        })
      );

      const tenantFilter: ITenantFilter = { name: null };
      dispatch(getAllTenants(tenantFilter));

      navigate("/tenants");
    } catch (error: any) {
      dispatch(
        setErrorState({
          message: "Error",
          description: error?.message || "Failed to create/update license",
          placement: "topRight",
          type: "error",
        })
      );
    } finally {
      dispatch(setLoadingState(false));
    }
  };


  return (
    <div className="add-edit-license-page-container">
      <div className="add-edit-license-page-content">
        <Card className="add-edit-license-page-card">
          <div className="page-header">
            <Button icon={<ArrowLeftOutlined />} onClick={handleCancel}>
              Back
            </Button>
            <h2>Add/Modify License</h2>
          </div>

          <Divider />

          <Stepper steps={steps} selected={currentStep} />

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            className="add-edit-license-form"
            preserve={false}
          >
            <div style={{ display: currentStep === 0 ? "block" : "none" }}>
              <BasicConfigurationStep
                packageNames={packageNames}
                tierOptions={tierOptions}
                selectedRestrictions={selectedRestrictions}
                onRestrictionChange={handleRestrictionChange}
                licenseSource={licenseSource}
                onLicenseSourceChange={handleLicenseSourceChange}
                subscriptionPrices={subscriptionPrices}
                productName={paddleProductName}
                onSubscriptionPriceSelect={handleSubscriptionPriceSelect}
                supportedFileFormats={supportedFileFormats}
              />
            </div>
            <div style={{ display: currentStep === 1 ? "block" : "none" }}>
              <LicenseSettingsStep />
            </div>
            <div style={{ display: currentStep === 2 ? "block" : "none" }}>
              <FeatureGroupsStep
                selectedFeatureGroups={selectedFeatureGroups}
                featureGroups={featureGroups || []}
                onOpenModal={() => setIsFeatureModalOpen(true)}
              />
            </div>

            <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Button onClick={handleCancel} htmlType="button">
                  Cancel
                </Button>
                <Space>
                  {currentStep > 0 && (
                    <Button onClick={handlePrevious} htmlType="button">
                      Previous
                    </Button>
                  )}
                  {currentStep < 2 ? (
                    <Button type="primary" onClick={handleNext} htmlType="button">
                      Next
                    </Button>
                  ) : (
                    <Button type="primary" htmlType="submit">
                      Create License
                    </Button>
                  )}
                </Space>
              </div>
            </Form.Item>
          </Form>
        </Card>
      </div>

      <FeatureSelectionModal
        isOpen={isFeatureModalOpen}
        onClose={() => setIsFeatureModalOpen(false)}
        onConfirm={handleFeatureConfirm}
        initialSelectedFeatures={selectedFeatureGroups}
        title="Select Feature Groups"
      />
    </div>
  );
};

export default AddEditLicensePage;
